import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AccountAdminCustomers from './AccountAdminCustomers';
import { AccountAdminUsers, AccountChangeUsername } from './AccountSettingsPanels';
import AccountCard from './AccountCard';
import AccountTileIcon from '../ui/AccountTileIcon';
import { AccountHubNav, AccountHubWelcome, OverviewStat, QuickAction } from './AccountHubParts';
import { AccountCustomerProfile } from './AccountCustomerPanels';
import { CustomerAccountPassword } from './CustomerAccountHub';

const ADMIN_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'profile', label: 'Profile' },
  { id: 'team', label: 'Team' },
  { id: 'customers', label: 'Customers' },
  { id: 'security', label: 'Security' },
];

const MEMBER_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'security', label: 'Security' },
];

export default function AdminAccountHub({
  isAdmin,
  displayName,
  username,
  email,
  phone,
  site,
  logout,
  changePassword,
  changeUsername,
  updateProfile,
  listUsers,
  listCustomers,
  createUser,
  updateUserRole,
  resetCustomerPassword,
}) {
  const location = useLocation();
  const tabs = isAdmin ? ADMIN_TABS : MEMBER_TABS;
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (location.hash === '#admin-customers' && isAdmin) {
      setTab('customers');
    }
  }, [location.hash, isAdmin]);
  const shownName = (displayName || username || email || 'Guest').trim();

  const roleBadge = isAdmin ? 'Store administrator' : 'Team member';

  const primaryAction = isAdmin ? (
    <Link to="/admin" className="btn btn-primary btn-sm rounded-pill px-3">
      Open catalog admin
    </Link>
  ) : (
    <Link to="/shop" className="btn btn-primary btn-sm rounded-pill px-3">
      Browse shop
    </Link>
  );

  const overviewStats = useMemo(() => {
    if (!isAdmin) {
      return (
        <div className="customer-account__stats customer-account__stats--single" role="list">
          <OverviewStat role="listitem" label="Access" value="Member" hint="Catalog admin is not included for this role" />
        </div>
      );
    }
    return (
      <div className="customer-account__stats" role="list">
        <OverviewStat role="listitem" label="Role" value="Admin" hint="Catalog, orders & site settings" />
        <OverviewStat
          role="listitem"
          label="Store orders"
          value="Manage"
          hint="Catalog admin → Orders & Analytics"
        />
        <OverviewStat
          role="listitem"
          label="Shoppers"
          value="Lookup"
          hint="Customers tab — registered accounts"
        />
      </div>
    );
  }, [isAdmin]);

  return (
    <div className={`customer-account customer-account--staff${isAdmin ? ' customer-account--admin' : ''}`}>
      <AccountHubWelcome
        eyebrow={isAdmin ? 'Admin workspace' : 'Team account'}
        name={shownName}
        email={email}
        roleBadge={roleBadge}
        primaryAction={primaryAction}
        onLogout={() => void logout()}
      />

      <AccountHubNav tabs={tabs} tab={tab} onTab={setTab} />

      <div className="customer-account__panel">
        {tab === 'overview' ? (
          <div className="customer-account__overview">
            {overviewStats}

            {isAdmin ? (
              <AccountCard
                id="admin-catalog"
                title="Catalog & orders"
                lead="Manage products, categories, customer orders, and site content."
                className="customer-account__featured-card"
              >
                <Link to="/admin" className="customer-account__catalog-cta">
                  <span className="customer-account__catalog-cta-icon" aria-hidden="true">
                    <AccountTileIcon name="admin" />
                  </span>
                  <span className="customer-account__catalog-cta-text">
                    <span className="customer-account__catalog-cta-title">Open catalog admin</span>
                    <span className="customer-account__catalog-cta-desc">Products · Orders · Analytics · Settings</span>
                  </span>
                  <span className="customer-account__catalog-cta-arrow" aria-hidden="true">
                    →
                  </span>
                </Link>
              </AccountCard>
            ) : (
              <AccountCard id="member-access" title="Your access" lead="Member accounts can use the storefront and this account page.">
                <p className="customer-account__member-note mb-0">
                  Ask an administrator if you need catalog or order-management access.
                </p>
              </AccountCard>
            )}

            <AccountCard
              id="admin-shortcuts"
              title={isAdmin ? 'Quick links' : 'Explore'}
              lead={isAdmin ? 'Jump to common admin and storefront pages.' : 'Explore the site while signed in.'}
            >
              <div className="customer-account__actions">
                {isAdmin ? (
                  <QuickAction
                    to="/admin"
                    icon="admin"
                    title="Catalog admin"
                    desc="Products, orders, analytics"
                    className="customer-account__action--primary"
                  />
                ) : null}
                <QuickAction to="/shop" icon="shop" title="Browse shop" desc="View collections" />
                <QuickAction to="/" icon="home" title="Home" desc="Featured & exhibitions" />
                <QuickAction
                  href={site?.whatsapp_href || 'https://wa.me/919680384332'}
                  icon="message"
                  title="WhatsApp"
                  desc="Support channel"
                  external
                />
              </div>
              {isAdmin ? (
                <p className="customer-account__overview-tip small text-muted mb-0 mt-3">
                  To place a personal order, use the storefront like any customer (bag and checkout). All{' '}
                  <strong>customer orders</strong> are managed in{' '}
                  <Link to="/admin">Catalog admin → Orders</Link>.
                </p>
              ) : null}
            </AccountCard>
          </div>
        ) : null}

        {isAdmin && tab === 'profile' ? (
          <AccountCard id="admin-profile" title="Your details" lead="Contact info on your staff account (receipts, notifications).">
            <AccountCustomerProfile
              embedded
              displayName={displayName}
              email={email}
              phone={phone}
              updateProfile={updateProfile}
            />
          </AccountCard>
        ) : null}

        {isAdmin && tab === 'team' ? (
          <AccountCard
            id="admin-team"
            title="Team accounts"
            lead="Add store staff as administrators or team members. Shop customers are not managed here."
          >
            <AccountAdminUsers
              embedded
              listUsers={listUsers}
              createUser={createUser}
              updateUserRole={updateUserRole}
              currentUsername={shownName}
            />
          </AccountCard>
        ) : null}

        {isAdmin && tab === 'customers' ? (
          <AccountCard
            id="admin-customers"
            title="Shop customers"
            lead="Everyone who registered on the storefront. View-only — not the same as team staff."
          >
            <AccountAdminCustomers
              embedded
              listCustomers={listCustomers}
              resetCustomerPassword={resetCustomerPassword}
            />
          </AccountCard>
        ) : null}

        {tab === 'security' ? (
          <div className="customer-account__security-stack">
            <AccountCard
              id="admin-username"
              title="Username"
              lead="Sign-in name for the admin portal. Requires your current password to change."
            >
              <AccountChangeUsername embedded changeUsername={changeUsername} currentUsername={username || shownName} />
            </AccountCard>
            <AccountCard
              id="admin-password"
              title="Password"
              lead="At least 8 characters. You stay signed in on this device after updating."
            >
              <CustomerAccountPassword changePassword={changePassword} />
            </AccountCard>
          </div>
        ) : null}
      </div>
    </div>
  );
}
